import styles from './AddressPicker.module.scss'
import { RefObject, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { AddressPickerProps } from './types'
import { formatAddress } from 'utils/user'
import { Banner, Icon } from '@boltenergy-be/design-system'
import { useOnClickOutside } from 'usehooks-ts'

const AddressPicker = ({ selectedContractId, setSelectedContractId, label, error, options }: AddressPickerProps) => {
  // Refs
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  // Constants
  const currentAddress =
    selectedContractId in options
      ? formatAddress(options[selectedContractId].serviceContracts.electricity.deliveryPoint.address)
      : undefined

  // Close drawer when clicking outside or pressing ESC
  useOnClickOutside(dropdownRef as RefObject<HTMLDivElement>, () => popoverRef.current?.hidePopover())

  /**
   * Hides dropdown on ESC button press when dropdown is open
   */
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        popoverRef.current?.hidePopover()
      }
    }

    document.addEventListener('keydown', handleEsc)

    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  }, [])

  if (!Object.keys(options).length || !currentAddress) {
    return (
      <Banner type="blocking" title={error.title}>
        <p>{error.description}</p>
      </Banner>
    )
  }

  return (
    <div className={styles['address-dropdown-group']}>
      <span className={styles.label}>{label}</span>

      <div ref={dropdownRef} className={styles['dropdown-wrapper']}>
        {Object.keys(options).length === 1 ? (
          <div className={styles['single-address']} title={currentAddress}>
            <span className={styles['current-address']}>{currentAddress}</span>
          </div>
        ) : (
          <>
            <button className={styles.toggle} type="button" aria-haspopup="true" popoverTarget="empty-popover" title={currentAddress}>
              <span className={styles['current-address']}>{currentAddress}</span>

              <Icon name="chevronDown" className={styles.icon} />
            </button>

            {/* TODO: This is currently a workaround to make the popover API support anchored positioning.
                  When CSS anchor positioning is widely supported, move popover="manual" to menu and update styles */}
            <div className={styles.popover} id="empty-popover" popover="manual" ref={popoverRef} />
            <menu className={styles.menu} id="address-menu">
              {Object.values(options).map((contract) => {
                const address = formatAddress(contract.serviceContracts.electricity.deliveryPoint.address)
                return address ? (
                  <li
                    key={contract.contractNumber}
                    className={classNames(styles['menu-item'], { [styles.selected]: contract.contractNumber === selectedContractId })}
                  >
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        setSelectedContractId(contract.contractNumber)
                        popoverRef.current?.hidePopover()
                      }}
                      title={address}
                    >
                      <span className={styles['selectable-address']}>{address}</span>
                    </button>
                  </li>
                ) : null
              })}
            </menu>
          </>
        )}
      </div>
    </div>
  )
}

export default AddressPicker
